/* Overlay.tsx */
/* An overlay component that greys out the page to draw focus to a feature */
import classNames from "classnames";
import noScroll from "no-scroll";
import React, { forwardRef, MouseEvent, ReactNode, useEffect } from "react";
import ReactDOM from "react-dom";
import { CSSTransition } from "react-transition-group";
import checkIsBrowser from "../helpers/index";
import styles from "./SearchOverlay.module.css";

const isBrowser: boolean = checkIsBrowser();

export interface OverlayProps {
  active?: boolean;
  ariaBusy?: boolean | null;
  children?: ReactNode;
  className?: string;
  dataTestId?: string;
  disableBackgroundScroll?: boolean;
  escapeKeyPress?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  onBackgroundClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onEnter?: () => void;
  onEntered?: () => void;
  onEntering?: () => void;
  onExit?: () => void;
  onExited?: () => void;
  onExiting?: () => void;
  onMouseEnterCallback?: () => void;
  transitionDuration?: number;
  transitionName?: string;
  zIndex?: number;
}

const defaultProps: Required<
  Pick<
    OverlayProps,
    | "active"
    | "ariaBusy"
    | "children"
    | "disableBackgroundScroll"
    | "escapeKeyPress"
    | "onBackgroundClick"
    | "onEnter"
    | "onEntered"
    | "onEntering"
    | "onExit"
    | "onExited"
    | "onExiting"
    | "onMouseEnterCallback"
    | "transitionDuration"
    | "transitionName"
    | "zIndex"
  >
> = {
  active: false,
  ariaBusy: null,
  children: null,
  disableBackgroundScroll: false,
  escapeKeyPress: () => {},
  onBackgroundClick: () => {},
  onEnter: () => {},
  onEntered: () => {},
  onEntering: () => {},
  onExit: () => {},
  onExited: () => {},
  onExiting: () => {},
  onMouseEnterCallback: () => {},
  transitionDuration: 200,
  transitionName: "tvg-overlay",
  zIndex: 0,
};

const Overlay = forwardRef<HTMLDivElement, OverlayProps>((props, ref) => {
  const {
    active,
    ariaBusy,
    children,
    className,
    dataTestId,
    disableBackgroundScroll,
    escapeKeyPress,
    inputRef,
    onBackgroundClick,
    onEnter,
    onEntered,
    onEntering,
    onExit,
    onExited,
    onExiting,
    onMouseEnterCallback,
    transitionDuration,
    transitionName,
  } = { ...defaultProps, ...props };

  /** Background click */
  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const { target, currentTarget } = event;
    if (typeof onBackgroundClick !== "function") return;

    if (target !== currentTarget) return;

    onBackgroundClick(event);
  };

  /** Unified keypress handler (works for DOM + React synthetic events) */
  const handleKeyPress = (event: KeyboardEvent) => {
    const { key } = event;
    const { keyCode } = event;
    const isEscape = key === "Escape" || key === "Esc" || keyCode === 27;
    if (!isEscape) return;

    escapeKeyPress?.(event as unknown as React.KeyboardEvent<HTMLDivElement>);

    inputRef?.current?.blur();
  };

  /** Background scrolling */
  useEffect(() => {
    if (disableBackgroundScroll && active) {
      noScroll.on();
    } else {
      noScroll.off();
    }

    return () => {
      if (disableBackgroundScroll) {
        noScroll.off();
      }
    };
  }, [active, disableBackgroundScroll]);

  /** Keypress event */
  useEffect(() => {
    const listener = (e: KeyboardEvent) => handleKeyPress(e);
    window.addEventListener("keyup", listener);

    return () => {
      window.removeEventListener("keyup", listener);
    };
  }, [active, escapeKeyPress]);

  const SearchOverlay = (
    <CSSTransition
      appear
      classNames={transitionName}
      in={active}
      mountOnEnter
      onEnter={onEnter}
      onEntered={onEntered}
      onEntering={onEntering}
      onExit={onExit}
      onExited={onExited}
      onExiting={onExiting}
      timeout={transitionDuration}
      unmountOnExit
    >
      <div
        aria-busy={ariaBusy ?? undefined}
        className={classNames(styles.searchOverlay, className)}
        data-testid={dataTestId}
        onClick={handleClick}
        onMouseEnter={onMouseEnterCallback}
        ref={ref}
      >
        {children}
      </div>
    </CSSTransition>
  );

  if (isBrowser) {
    return ReactDOM.createPortal(SearchOverlay, document.body);
  }

  return SearchOverlay;
});

Overlay.displayName = "Overlay";

export default Overlay;
